import {
  Breadcrumbs,
  Card,
  PageContent,
  PageHeader,
  Spinner,
} from '@campx-org/shared'
import BookOutlinedIcon from '@mui/icons-material/BookOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material'
import { useQuery } from 'react-query'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { EXAMS } from 'services'
import {
  Booklet,
  BreadcrumbLink,
  BundleBookletsResponse,
  Evaluator,
} from './types'

interface StatItemProps {
  label: string
  value: string
}

interface BundleInfo {
  serialNo: string
  stats: Array<{ label: string; value: string }>
  evaluators: Evaluator[]
}

interface BundleBookletsRendererProps {
  bundleInfo: BundleInfo | null
  booklets: Booklet[]
  isLoading: boolean
  isError: boolean
  breadcrumbs: BreadcrumbLink[]
  onBookletSelect: (booklet: Booklet) => void
  onRefetch: () => void
}

/** Renders a single label/value pair in the bundle detail grid. */
function StatItem({ label, value }: StatItemProps) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  )
}

/**
 * Pure renderer for the bundle booklets view. Contains zero flow-specific
 * conditions. Renders `bundleInfo.stats` as a mapped grid of `StatItem`s —
 * the wrapper decides which stats to include (external includes Subject and
 * Subject Code; internal only includes Type: "Internal").
 */
function BundleBookletsRenderer({
  bundleInfo,
  booklets,
  isLoading,
  isError,
  breadcrumbs,
  onBookletSelect,
  onRefetch,
}: BundleBookletsRendererProps) {
  return (
    <>
      <PageHeader title={<Breadcrumbs links={breadcrumbs} />} />

      <PageContent>
        <Stack spacing={2}>
          {isError && (
            <Alert severity="error" onClose={() => onRefetch()}>
              Failed to load bundle booklets.
            </Alert>
          )}

          {isLoading ? (
            <Spinner />
          ) : (
            <Card title="">
              {/* Top section: details left | evaluators right */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                mb={2.5}
              >
                {/* Left: bundle details in 2-col grid */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      rowGap: 2,
                      columnGap: 3,
                    }}
                  >
                    {bundleInfo?.stats.map((stat) => (
                      <StatItem
                        key={stat.label}
                        label={stat.label}
                        value={stat.value}
                      />
                    ))}
                    <StatItem
                      label="Total Booklets"
                      value={`${booklets.length}`}
                    />
                  </Box>
                </Box>

                {/* Vertical divider */}
                <Divider orientation="vertical" flexItem />

                {/* Right: evaluators as label/value grid */}
                <Box sx={{ flex: 1 }}>
                  {(bundleInfo?.evaluators || []).length > 0 ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        rowGap: 2,
                        columnGap: 3,
                      }}
                    >
                      {(bundleInfo?.evaluators || []).map((ev) => (
                        <StatItem
                          key={`${ev.evaluatorId}-${ev.evaluatorType}`}
                          label={ev.evaluatorType}
                          value={ev.evaluator?.fullName || '-'}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No evaluators assigned
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Booklets */}
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <BookOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                />
                <Typography variant="body2" fontWeight={700}>
                  Booklets
                </Typography>
                <Chip size="small" label={`${booklets.length}`} />
              </Stack>

              {booklets.length ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {booklets.map((booklet) => (
                    <Box
                      key={booklet.evaluationBookletId || booklet.id}
                      onClick={() => onBookletSelect(booklet)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        cursor: 'pointer',
                      }}
                    >
                      <BookOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary', flexShrink: 0 }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {booklet.barcode || `#${booklet.slno}`}
                      </Typography>
                      <ChevronRightIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary', flexShrink: 0 }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No booklets found.
                </Typography>
              )}
            </Card>
          )}
        </Stack>
      </PageContent>
    </>
  )
}

/**
 * External flow wrapper for the bundle booklets view.
 *
 * Fetches via `getBundleBooklets(bundleId)`. Builds `bundleInfo.stats` with
 * Serial No., Subject, Subject Code, and Type. Navigates to
 * `/admin/examinations/:examGroupId/bundles/:bundleId/booklets/:bookletId`
 * on booklet selection.
 */
export default function AdminBundleBookletsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { examGroupId, bundleId } = useParams()
  const [searchParams] = useSearchParams()

  const subjectName =
    location.state?.subjectName || searchParams.get('subjectName') || undefined
  const examGroup = location.state?.examGroup as
    | { groupName?: string }
    | undefined
  const subjectCodeParam = searchParams.get('subjectCode') || undefined

  const { data, isLoading, isError, refetch } =
    useQuery<BundleBookletsResponse>(
      ['admin-external-bundle-booklets', bundleId],
      () => EXAMS.adminBundleEvaluation.getBundleBooklets(bundleId || ''),
      { enabled: !!bundleId },
    )

  const booklets: Booklet[] = data?.booklets || []
  const evaluators: Evaluator[] = data?.evaluators || []
  const bundleSerialNo =
    location.state?.bundle?.bundleSerialNo || data?.bundle?.bundleSerialNo

  const bundleInfo: BundleInfo | null = data
    ? {
        serialNo: bundleSerialNo || '-',
        stats: [
          { label: 'Serial No.', value: data?.bundle?.bundleSerialNo || '-' },
          { label: 'Subject', value: data?.subject?.name || '-' },
          { label: 'Subject Code', value: data?.subject?.subjectCode || '-' },
          { label: 'Type', value: data?.bundle?.bundleType || '-' },
        ],
        evaluators,
      }
    : null

  const breadcrumbs: BreadcrumbLink[] = [
    { name: 'Exams', to: '/admin/examinations' },
    {
      name:
        data?.bundle?.examGroup?.groupName ||
        examGroup?.groupName ||
        'Examinations',
      to: `/admin/examinations/${examGroupId}/subjects`,
    },
    {
      name: data?.bundle?.subjectCode || subjectName || 'Subject',
      to: `/admin/examinations/${examGroupId}/subjects/bundles?subjectCode=${encodeURIComponent(
        data?.bundle?.subjectCode || subjectCodeParam || '',
      )}`,
    },
    { name: bundleSerialNo || 'Bundle', to: null },
  ]

  const handleBookletSelect = (booklet: Booklet) => {
    navigate(
      `/admin/examinations/${examGroupId}/bundles/${bundleId}/booklets/${booklet.id}`,
    )
  }

  return (
    <BundleBookletsRenderer
      bundleInfo={bundleInfo}
      booklets={booklets}
      isLoading={isLoading}
      isError={isError}
      breadcrumbs={breadcrumbs}
      onBookletSelect={handleBookletSelect}
      onRefetch={refetch}
    />
  )
}

/**
 * Internal flow wrapper for the bundle booklets view.
 *
 * Fetches via `getInternalBundleBooklets({ evaluationId, evaluationBundleId })`.
 * Builds `bundleInfo.stats` with only Serial No. and Type: "Internal" —
 * no Subject or Subject Code since internal evaluations are not subject-bound.
 * Navigates to `/admin/internal/:examGroupId/bundles/:evaluationBundleId/booklets/:evaluationBookletId`
 * on booklet selection.
 */
export function AdminInternalBundleBookletsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [bookletSearchParams] = useSearchParams()
  const { examGroupId, evaluationBundleId } = useParams()

  const evaluationId: string = bookletSearchParams.get('evaluationId') || ''
  const subjectName = location.state?.subjectName as string | undefined
  const examGroup = location.state?.examGroup as
    | { groupName?: string }
    | undefined

  const { data, isLoading, isError, refetch } =
    useQuery<BundleBookletsResponse>(
      ['admin-internal-bundle-booklets', evaluationBundleId, evaluationId],
      () =>
        EXAMS.adminBundleEvaluation.getInternalBundleBooklets({
          evaluationId,
          evaluationBundleId: evaluationBundleId || '',
        }),
      {
        enabled: !!evaluationBundleId && !!evaluationId,
      },
    )

  const booklets: Booklet[] = data?.booklets || []
  const evaluators: Evaluator[] = data?.evaluators || []
  const bundleSerialNo =
    location.state?.bundle?.bundleSerialNo || data?.bundle?.bundleSerialNo

  const bundleInfo: BundleInfo | null = data
    ? {
        serialNo: bundleSerialNo || '-',
        stats: [
          { label: 'Serial No.', value: data?.bundle?.bundleSerialNo || '-' },
          { label: 'Type', value: 'Internal' },
        ],
        evaluators,
      }
    : null

  const breadcrumbs: BreadcrumbLink[] = [
    { name: 'Exams', to: '/admin/examinations' },
    {
      name: examGroup?.groupName || 'Examination',
      to: `/admin/internal/${examGroupId}/subjects`,
    },
    {
      name: subjectName || 'Subject',
      to: `/admin/internal/${examGroupId}/subjects/bundles?evaluationId=${encodeURIComponent(
        evaluationId,
      )}`,
    },
    { name: bundleSerialNo || 'Bundle', to: null },
  ]

  const handleBookletSelect = (booklet: Booklet) => {
    const evaluationBookletId =
      booklet.evaluationBookletId || String(booklet.id)
    navigate(
      `/admin/internal/${examGroupId}/bundles/${evaluationBundleId}/booklets/${evaluationBookletId}`,
      { state: { evaluationId, examGroup, examCategory: 'internal' } },
    )
  }

  return (
    <BundleBookletsRenderer
      bundleInfo={bundleInfo}
      booklets={booklets}
      isLoading={isLoading}
      isError={isError}
      breadcrumbs={breadcrumbs}
      onBookletSelect={handleBookletSelect}
      onRefetch={refetch}
    />
  )
}
